import { get, post, put, del } from '../utils/request';

// Collaboration status enum
export enum CollaborationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
}

// Collaboration role enum
export enum CollaborationRole {
  CO_COUNSEL = 'CO_COUNSEL',
  CONSULTANT = 'CONSULTANT',
  REVIEWER = 'REVIEWER',
  OBSERVER = 'OBSERVER',
}

// Collaboration permission enum
export enum CollaborationPermission {
  VIEW = 'view',
  EDIT = 'edit',
  MANAGE = 'manage',
}

// Collaboration interface
export interface Collaboration {
  id: number;
  case_id?: number;
  lead_id?: number;
  initiator_id: number;
  initiator_name?: string;
  collaborator_id: number;
  collaborator_name?: string;
  role: CollaborationRole;
  permissions: CollaborationPermission[];
  notes?: string;
  status: CollaborationStatus;
  created_at: string;
  updated_at: string;
}

// Invite collaborator request interface
export interface InviteCollaboratorRequest {
  collaborator_ids: number[];
  role: CollaborationRole;
  permissions: CollaborationPermission[];
  description?: string;
  send_notification: boolean;
}

// Update collaborator request interface
export interface UpdateCollaboratorRequest {
  role?: CollaborationRole;
  permissions?: CollaborationPermission[];
  description?: string;
}

/**
 * Invite collaborator to case
 */
export const inviteCollaborator = async (
  caseId: number,
  data: InviteCollaboratorRequest
): Promise<Collaboration[]> => {
  return post<Collaboration[]>(`/cases/${caseId}/collaborators`, data);
};

/**
 * Get collaborators for case
 */
export const getCollaborators = async (caseId: number): Promise<Collaboration[]> => {
  return get<Collaboration[]>(`/cases/${caseId}/collaborators`);
};

/**
 * Update collaborator
 */
export const updateCollaborator = async (
  caseId: number,
  userId: number,
  data: UpdateCollaboratorRequest
): Promise<Collaboration> => {
  return put<Collaboration>(`/cases/${caseId}/collaborators/${userId}`, data);
};

/**
 * Remove collaborator
 */
export const removeCollaborator = async (
  caseId: number,
  userId: number
): Promise<{ msg: string }> => {
  return del<{ msg: string }>(`/cases/${caseId}/collaborators/${userId}`);
};

/**
 * Get my collaborations (cases I'm collaborating on)
 */
export const getMyCollaborations = async (params?: {
  status?: CollaborationStatus;
  page?: number;
  page_size?: number;
}): Promise<Collaboration[]> => {
  return get<Collaboration[]>('/collaborations/my', { params });
};

/**
 * Get collaboration by ID
 */
export const getCollaborationById = async (id: number): Promise<Collaboration> => {
  return get<Collaboration>(`/collaborations/${id}`);
};

/**
 * Accept collaboration invitation
 */
export const acceptCollaboration = async (id: number): Promise<Collaboration> => {
  return post<Collaboration>(`/collaborations/${id}/accept`, {});
};

/**
 * Decline collaboration invitation
 */
export const declineCollaboration = async (id: number): Promise<Collaboration> => {
  return post<Collaboration>(`/collaborations/${id}/decline`, {});
};

/**
 * Check if user has permission for case
 */
export const checkCasePermission = async (
  caseId: number,
  permission: CollaborationPermission
): Promise<{ has_permission: boolean }> => {
  return get<{ has_permission: boolean }>(`/cases/${caseId}/check-permission`, {
    params: { permission },
  });
};
